import { useFlags, useLDClient, useLDClientError } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';

import { initDatadogLogs, toggleDatadogLogging } from 'helpers/logger';

export type RecentProjectsConfigType = {
  maxCount: number;
  prereqProjects: number;
  prereqWorkspaces: number;
};

/**
 * When adding/removing feature flags in LaunchDarkly, update this type as well.
 * It will ensure that we don't accidentally do something like:
 *
 * const { misspelledFeatureFlagName } = useFlags();
 *
 * Using the useFeatureFlag() hook will raise a compilation error if a flag is misspelled.
 */
export type FeatureFlags = Partial<{
  // Please keep this list alphabetized. Makes finding flags easier.
  adjustGanttBarHeights: boolean;
  allProjectsTableView: boolean;
  autoLayout2: boolean;
  autoMoveEdgeToAvailableSpot: boolean;
  bannerUrl: string;
  bigGanttVersion: 'legacyGantt' | 'reactGantt9';
  canary: boolean;
  compactHomepageCards: boolean;
  contextItemLimit: number;
  customQualityScoreTest: boolean;
  customQualityScoring: boolean;
  enableKeyboardShortcuts: boolean;
  expandRelationshipIdRows: boolean;
  demoModeEnabled: boolean;
  disableDatadogLogging: boolean;
  drivingRelationships: boolean;
  drivingPath: boolean;
  enableBookmarksImportFromMs: boolean;
  enableBulkInviteUsersToOrg: boolean;
  enableBulkProgressUpdates: boolean;
  enableDownloadPdf: boolean;
  enableDragAndDropHintsForSs: boolean;
  enableEmbeddedManny: boolean;
  enableFilterByCustomCode: boolean;
  enableGanttLayoutToggle: boolean;
  enableInspectPathRelationships: boolean;
  enableMagicCursor: boolean;
  enableMannyDockedMode: boolean;
  enableMannyFeed: boolean;
  enableMannyHtmlWidget: boolean;
  enableMannyOrganizationPromptLibrary: boolean;
  enableMannyProgressUpdateInsight: boolean;
  enableMannyProjectBrief: boolean;
  enableMannyPromptCatalog: boolean;
  enableMannySaveChat: boolean;
  enableMannyWendyUnifiedLauncher: boolean;
  enableMannyInternalFeatures: boolean;
  enableMannyMutations: boolean;
  enableMannyPhaseTwo: boolean;
  enableMultiOrg: boolean;
  enableP6XlsxExport: boolean;
  enablePpcDashboardCriticalPathFilter: boolean;
  enablePlaneraMasterclass: boolean;
  enableReImportOfPreviousVersions: boolean;
  enableResourceTotalUnits: boolean;
  enableSchedulerRole: boolean;
  enableSuccessorVersionImport: boolean;
  enableTransferDraftProjects: boolean;
  enableTreeNavigationSearch: boolean;
  enableUpdatesListFutureWork: boolean;
  enableUpdatesListKeepCompleted: boolean;
  enableUpdatesListPanel: boolean;
  enableVarianceAgainstVersion: boolean;
  enableWbsManagementMode: boolean;
  enableWsCreationFromImport: boolean;
  deleteElementUndoDisableCount: number;
  exportAllVersions: boolean;
  freeFormNotes: boolean;
  freeFormNotesImageEmbeds: boolean;
  ganttInspectOrganizeMode: boolean;
  ganttPdfPageSizes: boolean;
  hidePlaneraLogoInGanttPdf: boolean;
  imageEmbed: boolean;
  inheritVarianceCodes: boolean;
  iPadAccess: boolean;
  linkedCopyPaste: boolean;
  logProjectRoomUpdatesToDatadog: boolean;
  manny: boolean;
  mannyCatchUpActionThreshold: number;
  mannyImageAttachments: boolean;
  maxElementDuration: number;
  maxElementsForIpad: number;
  maxElementsPerGroup: number;
  mergeElementsWithMatchingId: boolean;
  minimumAppVersion: string;
  miroEmbed: boolean;
  newGroupConnectorColors: boolean;
  newStatusPanel: boolean;
  newStubRules: boolean;
  orgProjectProgressDashboard: boolean;
  orgResourceRollupReports: boolean;
  organizeResourcesByProjectInRollupReport: boolean;
  organizeByDisableOptionLength: number;
  organizationFields: boolean;
  organizationSwitcherSearchBar: boolean;
  p6ImportWizard: boolean;
  p6Merge: boolean;
  p6S3Export: boolean;
  p6SnapshotDownload: boolean;
  p6XerExport: boolean;
  p6XlsxExportAvailableFromDate: string;
  pasteActivityIdsFromExcel: boolean;
  pdfPageLimit: number;
  pdfReactGantt: boolean;
  ppcDashboard: boolean;
  projectDashboardPrintSupport: boolean;
  projectInfoPopupFsListV2: boolean;
  projectLimitsMaxCount: number;
  projectLimitsWarningThreshold: number;
  projectPerformanceGanttVersion: 'legacyGantt' | 'reactGantt9';
  resourceUsageDayModeMaxWeeks: number;
  rollingLookaheads: boolean;
  runCpmPreAuditDialogV2: boolean;
  showActivityId: boolean;
  showStaleMsWarning: boolean;
  stripExpectedFinishDate: boolean;
  tasksUi: boolean;
  universalSearch: boolean;
  enableUniversalSearchNotesComments: boolean;
  varianceCodesForNotStarted: boolean;
  workingSchedules: boolean;
  newRelationshipContextMenuBehavior: boolean;
  activityPanelDataDateUpdate: boolean;
  // Please keep this list alphabetized. Makes finding flags easier.
}>;

export const useFeatureFlags = useFlags<FeatureFlags>;

export function useMonitorLD() {
  const client = useLDClient();
  const error = useLDClientError();
  const { canary, disableDatadogLogging } = useFeatureFlags();

  useEffect(() => {
    const details = client?.variationDetail('canary');
    const loaded = details !== undefined;
    const ok = !!details?.value;
    if (loaded && !ok) {
      const reason = details?.reason ? JSON.stringify(details?.reason) : 'unknown ld error';
      logger.error('LD canary issue', {
        error: new Error(`LD canary issue: ${reason}`),
        extra: { reason },
        rum: {
          context: {
            feature: 'launchdarkly',
            action: 'canary_issue'
          }
        }
      });
    }
  }, [/* effect dep */ canary, client]);

  useEffect(() => {
    if (disableDatadogLogging) {
      toggleDatadogLogging(false);
    } else {
      toggleDatadogLogging(true);
      initDatadogLogs();
    }
  }, [disableDatadogLogging]);

  useEffect(() => {
    if (error) {
      logger.error('LD error', {
        error: error,
        rum: {
          context: {
            feature: 'launchdarkly',
            action: 'client_error'
          }
        }
      });
    }
  }, [error]);
}
